import { useNavigate } from 'react-router-dom'
import {
    RiAddLine,
    RiDeleteBinLine,
    RiPrinterLine,
    RiFileAddLine,
    RiEdit2Line,
    RiSettings3Line,
    RiInboxArchiveLine,
    RiArrowRightLine,
    RiCloseLine,
    RiArrowDownSLine,
    RiUploadCloudLine,
    RiGoogleLine,
} from 'react-icons/ri'
import { useEffect, useRef, useState } from 'react'
import { useProjects, useDeleteProject } from '@/api/useProjects'
import { Button, Spinner } from '@/components/ui'
import { ProjectThumbnail } from '@/components/projects/ProjectThumbnail'
import { CanvaImportModal } from '@/components/features/CanvaImportModal/CanvaImportModal'
import { DocumentImportModal } from '@/components/features/DocumentImportModal/DocumentImportModal'
import { GoogleDriveImportModal } from '@/components/features/GoogleDriveImportModal/GoogleDriveImportModal'
import { ROUTES, buildRoute } from '@/constants/routes'
import toast from 'react-hot-toast'
import styles from './ProjectsList.module.css'

const BANNER_DISMISSED_KEY = 'impresiones_banner_dismissed'

const howItWorks = [
    {
        icon: RiEdit2Line,
        step: '1',
        title: 'Crea un proyecto',
        description: 'Elige un formato (tarjeta, volante, etc.) o usa dimensiones personalizadas para empezar a diseñar.',
    },
    {
        icon: RiSettings3Line,
        step: '2',
        title: 'Diseña en el editor',
        description: 'Usa nuestro editor visual para agregar texto, imágenes y darle forma a tu diseño profesional.',
    },
    {
        icon: RiInboxArchiveLine,
        step: '3',
        title: 'Ordena tu impresión',
        description: 'Selecciona material, tamaño y cantidad. Recibe tus impresos en cualquier lugar del Perú.',
    },
]

export function ProjectsList() {
    const navigate = useNavigate()
    const { data: projects, isLoading } = useProjects()
    const deleteProject = useDeleteProject()

    const [bannerDismissed, setBannerDismissed] = useState(
        () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
    )
    const [canvaModalOpen, setCanvaModalOpen] = useState(false)
    const [documentModalOpen, setDocumentModalOpen] = useState(false)
    const [gdriveModalOpen, setGdriveModalOpen] = useState(false)
    const [importMenuOpen, setImportMenuOpen] = useState(false)

    const importWrapperRef = useRef<HTMLDivElement>(null)

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!importMenuOpen) return

        const handleClick = (e: MouseEvent) => {
            if (importWrapperRef.current && !importWrapperRef.current.contains(e.target as Node)) {
                setImportMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [importMenuOpen])

    const handleDismissBanner = () => {
        setBannerDismissed(true)
        localStorage.setItem(BANNER_DISMISSED_KEY, 'true')
    }

    const handleDelete = (id: string, name: string) => {
        if (confirm(`Eliminar proyecto "${name}"?`)) {
            deleteProject.mutate(id, {
                onSuccess: () => toast.success('Proyecto eliminado'),
                onError: () => toast.error('Error al eliminar'),
            })
        }
    }

    const showBanner = !bannerDismissed && (!projects || projects.length < 3)

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <Spinner size="lg" />
                </div>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {/* Welcome banner */}
            {showBanner && (
                <section className={styles.banner}>
                    <button
                        className={styles.bannerClose}
                        onClick={handleDismissBanner}
                        aria-label="Cerrar banner"
                    >
                        <RiCloseLine />
                    </button>

                    <div className={styles.bannerHeader}>
                        <h2 className={styles.bannerTitle}>
                            Diseña e imprime con facilidad
                        </h2>
                        <p className={styles.bannerDesc}>
                            Crea diseños profesionales para tarjetas de presentación, volantes, afiches y más.
                            Nosotros nos encargamos de la impresión y el envío.
                        </p>
                    </div>

                    <div className={styles.stepsRow}>
                        {howItWorks.map((item, i) => (
                            <div key={item.step} className={styles.stepCard}>
                                <div className={styles.stepNumber}>{item.step}</div>
                                <div className={styles.stepIcon}>
                                    <item.icon />
                                </div>
                                <h3 className={styles.stepTitle}>{item.title}</h3>
                                <p className={styles.stepDesc}>{item.description}</p>
                                {i < howItWorks.length - 1 && (
                                    <div className={styles.stepConnector} aria-hidden="true" />
                                )}
                            </div>
                        ))}
                    </div>

                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => navigate(ROUTES.newProject)}
                    >
                        Crear mi primer proyecto <RiArrowRightLine />
                    </Button>
                </section>
            )}

            {/* Page header */}
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Mis Proyectos</h1>
                    <p className={styles.subtitle}>
                        {projects && projects.length > 0
                            ? `${projects.length} proyecto${projects.length !== 1 ? 's' : ''} en tu cuenta`
                            : 'Crea tu primer diseño para empezar'}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    {/* Import dropdown */}
                    <div className={styles.importWrapper} ref={importWrapperRef}>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => setImportMenuOpen((prev) => !prev)}
                        >
                            Importar <RiArrowDownSLine />
                        </Button>

                        {importMenuOpen && (
                            <div className={styles.importMenu}>
                                <button
                                    className={styles.importMenuItem}
                                    onClick={() => {
                                        setImportMenuOpen(false)
                                        setCanvaModalOpen(true)
                                    }}
                                >
                                    <span className={`${styles.importMenuIcon} ${styles.importMenuIconCanva}`}>
                                        <img src="/canva.png" alt="Canva" className={styles.importMenuImg} />
                                    </span>
                                    <span className={styles.importMenuText}>
                                        <span className={styles.importMenuLabel}>Canva</span>
                                        <span className={styles.importMenuDesc}>Importar desde tu cuenta</span>
                                    </span>
                                </button>

                                <button
                                    className={styles.importMenuItem}
                                    onClick={() => {
                                        setImportMenuOpen(false)
                                        setDocumentModalOpen(true)
                                    }}
                                >
                                    <span className={`${styles.importMenuIcon} ${styles.importMenuIconDocument}`}>
                                        <RiUploadCloudLine />
                                    </span>
                                    <span className={styles.importMenuText}>
                                        <span className={styles.importMenuLabel}>Subir archivo</span>
                                        <span className={styles.importMenuDesc}>PNG, JPG o WebP</span>
                                    </span>
                                </button>

                                <button
                                    className={styles.importMenuItem}
                                    onClick={() => {
                                        setImportMenuOpen(false)
                                        setGdriveModalOpen(true)
                                    }}
                                >
                                    <span className={`${styles.importMenuIcon} ${styles.importMenuIconDrive}`}>
                                        <RiGoogleLine />
                                    </span>
                                    <span className={styles.importMenuText}>
                                        <span className={styles.importMenuLabel}>Google Drive</span>
                                        <span className={styles.importMenuDesc}>Seleccionar desde Drive</span>
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>

                    <Button variant="primary" size="md" onClick={() => navigate(ROUTES.newProject)}>
                        <RiAddLine /> Nuevo Proyecto
                    </Button>
                </div>
            </div>

            {!projects || projects.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>
                        <RiFileAddLine />
                    </div>
                    <h3 className={styles.emptyTitle}>Aún no tienes proyectos</h3>
                    <p className={styles.emptyDesc}>
                        Comienza creando un nuevo proyecto. Puedes elegir entre diferentes formatos o usar dimensiones personalizadas.
                    </p>
                    <Button variant="primary" size="lg" onClick={() => navigate(ROUTES.newProject)}>
                        <RiAddLine /> Crear mi primer proyecto
                    </Button>
                </div>
            ) : (
                <div className={styles.grid}>
                    {projects.map((project) => (
                        <div key={project.id} className={styles.card}>
                            <div
                                className={styles.thumbnail}
                                onClick={() => navigate(buildRoute.editor(project.id))}
                            >
                                {project.thumbnail_url ? (
                                    <img src={project.thumbnail_url} alt={project.name} />
                                ) : (
                                    <ProjectThumbnail
                                        projectId={project.id}
                                        widthMm={project.width_mm}
                                        heightMm={project.height_mm}
                                        placeholder={
                                            <div className={styles.placeholderPreview}>
                                                <div className={styles.placeholderPage}>
                                                    <div className={styles.placeholderLine} style={{ width: '70%' }} />
                                                    <div className={styles.placeholderLine} style={{ width: '50%' }} />
                                                    <div className={styles.placeholderBlock} />
                                                    <div className={styles.placeholderLine} style={{ width: '60%' }} />
                                                </div>
                                                <span className={styles.placeholderSize}>
                                                    {project.width_mm}×{project.height_mm}mm
                                                </span>
                                            </div>
                                        }
                                    />
                                )}
                            </div>
                            <div className={styles.cardInfo}>
                                <h3 className={styles.cardTitle}>{project.name}</h3>
                                <p className={styles.cardMeta}>
                                    {project.document_type_name || 'Personalizado'} · {project.page_count} página{project.page_count !== 1 ? 's' : ''}
                                </p>
                            </div>
                            <button
                                className={styles.printButton}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    navigate(buildRoute.orderCheckout(project.id))
                                }}
                                title="Imprimir"
                            >
                                <RiPrinterLine />
                            </button>
                            <button
                                className={styles.deleteButton}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleDelete(project.id, project.name)
                                }}
                                title="Eliminar"
                            >
                                <RiDeleteBinLine />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <CanvaImportModal
                visible={canvaModalOpen}
                onClose={() => setCanvaModalOpen(false)}
            />
            <DocumentImportModal
                visible={documentModalOpen}
                onClose={() => setDocumentModalOpen(false)}
            />
            <GoogleDriveImportModal
                visible={gdriveModalOpen}
                onClose={() => setGdriveModalOpen(false)}
            />
        </div>
    )
}
